import React, { useEffect, useRef, useState } from "react";
import {
  IoAlertCircleOutline,
  IoPlayOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";

const VIDEO_MIME_TYPES = {
  m4v: "video/mp4",
  mov: "video/quicktime",
  mp4: "video/mp4",
  ogv: "video/ogg",
  webm: "video/webm",
};

const getVideoMimeType = (mediaName, mimeType) => {
  if (mimeType?.startsWith("video/")) return mimeType;

  const cleanName = String(mediaName || "")
    .split("?")[0]
    .split("#")[0];
  const extension = cleanName.split(".").pop().toLowerCase();

  return VIDEO_MIME_TYPES[extension];
};

export default function NftVideo({
  autoPlay = true,
  className = "img-fluid",
  compact = false,
  file,
  label = "Video NFT",
  loop = true,
  mediaName,
  mimeType,
  poster,
  preload = "metadata",
  src,
}) {
  const videoRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState("");
  const [isMuted, setIsMuted] = useState(true);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const videoSrc = objectUrl || src;
  const resolvedMimeType = getVideoMimeType(
    mediaName || file?.name || src,
    mimeType || file?.type,
  );

  useEffect(() => {
    if (!file) {
      setObjectUrl("");
      return undefined;
    }

    const nextObjectUrl = URL.createObjectURL(file);
    setObjectUrl(nextObjectUrl);

    return () => {
      URL.revokeObjectURL(nextObjectUrl);
    };
  }, [file]);

  useEffect(() => {
    setIsMuted(true);
    setPlaybackBlocked(false);
    setMediaError(false);

    if (videoRef.current) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
      videoRef.current.load();
    }
  }, [videoSrc]);

  const tryMutedAutoplay = (event) => {
    if (!autoPlay || !isMuted || !event.currentTarget.paused) return;

    event.currentTarget.defaultMuted = true;
    event.currentTarget.muted = true;
    const playAttempt = event.currentTarget.play();
    playAttempt?.catch(() => setPlaybackBlocked(true));
  };

  const toggleSound = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.defaultMuted = nextMuted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);

    try {
      await video.play();
      setPlaybackBlocked(false);
    } catch (error) {
      video.defaultMuted = true;
      video.muted = true;
      setIsMuted(true);
      setPlaybackBlocked(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      toggleSound(event);
    }
  };

  const preventMediaMenu = (event) => {
    event.preventDefault();
  };

  const status = mediaError
    ? {
        icon: <IoAlertCircleOutline aria-hidden="true" />,
        text: "Preview unavailable on this device",
      }
    : playbackBlocked
      ? {
          icon: <IoPlayOutline aria-hidden="true" />,
          text: "Tap video to play with sound",
        }
      : isMuted
        ? {
            icon: <IoVolumeMuteOutline aria-hidden="true" />,
            text: "Tap video for sound",
          }
        : {
            icon: <IoVolumeHighOutline aria-hidden="true" />,
            text: "Sound on · tap to mute",
          };

  return (
    <div
      className={`nft-video-player${compact ? " nft-video-player--compact" : ""}`}
      onContextMenu={preventMediaMenu}
    >
      <video
        ref={videoRef}
        className={`${className} nft-video-player__media`}
        autoPlay={autoPlay}
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        loop={loop}
        muted={isMuted}
        playsInline
        poster={poster || undefined}
        preload={preload}
        role="button"
        tabIndex={0}
        aria-label={`${label}. ${status.text}`}
        aria-pressed={!isMuted}
        onCanPlay={tryMutedAutoplay}
        onClick={toggleSound}
        onError={() => setMediaError(true)}
        onKeyDown={handleKeyDown}
        onContextMenu={preventMediaMenu}
      >
        {videoSrc && (
          <source
            key={videoSrc}
            src={videoSrc}
            type={resolvedMimeType || undefined}
          />
        )}
        Your browser does not support inline video playback.
      </video>

      <button
        type="button"
        className={`nft-video-player__sound${mediaError ? " nft-video-player__sound--error" : ""}`}
        aria-label={status.text}
        onClick={toggleSound}
      >
        {status.icon}
        <span>{status.text}</span>
      </button>
    </div>
  );
}
